import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Instagram,
  MessageCircle,
  Phone,
  Shield,
  FileText,
} from 'lucide-react';

const Footer = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const currentYear = new Date().getFullYear();

  const content = {
    description: isAr
      ? 'خدمات سوبر كلاسيكي لغسيل السيارات المتنقلة في الكويت. احترافية عالية تصلك حتى باب بيتك.'
      : 'super classy mobile car wash services in Kuwait. Professional detailing at your doorstep.',
    quickLinks: isAr ? 'روابط سريعة' : 'Quick Links',
    contactUs: isAr ? 'اتصل بنا' : 'Contact Us',
    legal: isAr ? 'قانوني' : 'Legal',
    links: [
      { name: isAr ? 'الخدمات' : 'Services', path: '/services' },
      { name: isAr ? 'الباقات' : 'Packages', path: '/packages' },
      { name: isAr ? 'المعرض' : 'Gallery', path: '/gallery' },
      { name: isAr ? 'التقييمات' : 'Reviews', path: '/reviews' },
    ],
    whatsapp: isAr ? 'دردشة عبر واتساب' : 'Chat on WhatsApp',
    privacy: isAr ? 'سياسة الخصوصية' : 'Privacy Policy',
    terms: isAr ? 'الشروط والأحكام' : 'Terms & Conditions',
    rights: isAr
      ? `© ${currentYear} سوبر كلاسيكي لغسيل السيارات الكويت. جميع الحقوق محفوظة.`
      : `© ${currentYear} Super Classy Car Wash Kuwait. All rights reserved.`,
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      isAr
        ? 'مرحباً سوبر كلاسيكي، أود حجز خدمة غسيل سيارات.'
        : 'Hi Super Classy, I want to book a car wash service.',
    );
    window.open(`https://wa.me/96550040026?text=${message}`, '_blank');
  };

  return (
    <footer
      className='bg-gradient-to-b from-gray-900 to-black text-white mt-auto'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className='section-padding py-12'>
        <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8'>
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div
              className={`flex items-center mb-4 ${isAr ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
            >
              <img
                src='/assets/images/logo/logo.png'
                alt='Super Classy Logo'
                className='h-8 w-auto'
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    'https://via.placeholder.com/32/ffffff/000000?text=SC';
                }}
              />
              <span className='text-xl font-bold'>Super Classy</span>
            </div>
            <p className='text-gray-400 mb-4 leading-relaxed'>
              {content.description}
            </p>
            <div
              className={`flex ${isAr ? 'space-x-reverse space-x-4' : 'space-x-4'}`}
            >
              <a
                href='https://instagram.com/superclassykw'
                target='_blank'
                rel='noopener noreferrer'
                className='p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors'
              >
                <Instagram className='h-5 w-5' />
              </a>
              <a
                href='https://tiktok.com/@superclassykw'
                target='_blank'
                rel='noopener noreferrer'
                className='p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors'
              >
                <MessageCircle className='h-5 w-5' />
              </a>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className='text-lg font-semibold mb-4 border-b border-gray-800 pb-2'>
              {content.quickLinks}
            </h3>
            <ul className='space-y-2'>
              {content.links.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className='text-gray-400 hover:text-white transition-colors'
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className='text-lg font-semibold mb-4 border-b border-gray-800 pb-2'>
              {content.contactUs}
            </h3>
            <div className='space-y-3'>
              <a
                href='tel:+96550040026'
                className={`flex items-center text-gray-400 hover:text-white transition-colors ${isAr ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              >
                <Phone className='h-5 w-5' />
                <span dir='ltr'>+965 5004 0026</span>
              </a>
              <button
                onClick={openWhatsApp}
                className={`flex items-center text-green-400 hover:text-green-300 transition-colors ${isAr ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              >
                <MessageCircle className='h-5 w-5' />
                <span>{content.whatsapp}</span>
              </button>
            </div>
          </motion.div>

          {/* Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className='text-lg font-semibold mb-4 border-b border-gray-800 pb-2'>
              {content.legal}
            </h3>
            <div className='space-y-3'>
              <Link
                to='#'
                className={`flex items-center text-gray-400 hover:text-white transition-colors ${isAr ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              >
                <Shield className='h-5 w-5' />
                <span>{content.privacy}</span>
              </Link>
              <Link
                to='#'
                className={`flex items-center text-gray-400 hover:text-white transition-colors ${isAr ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              >
                <FileText className='h-5 w-5' />
                <span>{content.terms}</span>
              </Link>
            </div>
          </motion.div>
        </div>

        <div className='border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm'>
          <p>{content.rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
